import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {AddressModel, ApplicantModel, KeyIdentifierModel} from '@service/models';
import {AppConfigService} from '@service/config/app-config.service';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {NgForm} from '@angular/forms';
import {IdentityInfoComponent} from '../identity-info/identity-info.component';
import {EmploymentInfoComponent} from '../employment-info/employment-info.component';
import {ResidenceInfoComponent} from '../residence-info/residence-info.component';
import {DateComponent} from '@shared/form-components/date/date.component';
import {SsnComponent} from '@shared/form-components/ssn/ssn.component';
import {ApplicantType} from '@service/enum/process-state.enum';
import * as moment from 'moment';
import {ContentService} from '../../../constants/content.service';
import {getInstitutionId} from '../../../constants/constant';
import {environment} from '@env/environment';

@Component({
    selector: 'cs-authorized-user',
    templateUrl: './authorized-user.component.html',
    styleUrls: ['./authorized-user.component.scss']
})
export class AuthorizedUserComponent implements OnInit, OnChanges {
    @ViewChild('f') f: NgForm;
    @ViewChild('dob') dob: DateComponent;
    @ViewChild('ssn') ssn: SsnComponent;
    @ViewChild('employmentInfo') employmentInfo: EmploymentInfoComponent;
    @ViewChild('residenceInfo') residenceInfo: ResidenceInfoComponent;
    @ViewChild('identityInfo') identityInfo: IdentityInfoComponent;
    @Input() primaryApplicant: ApplicantModel = new ApplicantModel();
    @Input() applicant: ApplicantModel = new ApplicantModel();

    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';

    appConfig = AppConfigService.config;
    keyIdentifier: KeyIdentifierModel;
    finalSubmitPageConfig: any;
    miscellaneousFieldsConfig: any;
    maxDobDate: Date = new Date();
    minDobDate: Date = new Date();
    citizenshipOptions = [];
    citizenShip = [];
    coBoCitizenInd = '';
    isYouthAccount: boolean;

    constructor(public contentService: ContentService,
                private sharedKeyDataService: SharedkeyDataService) {
        sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;

                if (this.keyIdentifier.loanPurposeConfig) {
                    // TODO: remove backward compatability after moving to S3
				    this.finalSubmitPageConfig = this.keyIdentifier.loanPurposeConfig.finalSubmitPages?.page1 || this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                    this.miscellaneousFieldsConfig = this.keyIdentifier.loanPurposeConfig.finalSubmitPages.miscellaneousFields;
                    this.isYouthAccount = this.keyIdentifier.loanPurposeConfig.title.toLowerCase().includes('youth');
                }
            });

        if (this.keyIdentifier.loanPurposeConfig && this.isYouthAccount) {
            this.minDobDate = moment().subtract(120, 'years').toDate();
            this.maxDobDate = moment().subtract(18, 'years').toDate();
        } else {
            this.minDobDate = moment().subtract(this.keyIdentifier.loanPurposeConfig.maxAge || 120, 'years').toDate();
            // An authorized user is always an adult, whatever minAge the product configures.
            this.maxDobDate = moment().subtract(18, 'years').toDate();
        }
    }

    ngOnInit() {
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes.primaryApplicant && changes.primaryApplicant.currentValue) {
            this.primaryApplicant = changes.primaryApplicant.currentValue;
        }

        if (changes.applicant && changes.applicant.currentValue) {
            this.applicant = changes.applicant.currentValue || new ApplicantModel();
            this.coBoCitizenInd = this.applicant.citizenshipStatusDesc;
            if (this.applicant?.dob) {
                this.applicant.dateOfBirth = new Date(this.applicant.dob);
            }
        }

    }

    runValidation() {
        this.f.form.markAllAsTouched();
        this.ssn?.touch();
        this.dob?.touch();

        const checkValids = [];
        checkValids.push(this.f.valid);
        if (this.identityInfo && this.identityInfo.f) {
            this.identityInfo.runValidation();
            checkValids.push(this.identityInfo.f.form.valid);
        }

        if (this.finalSubmitPageConfig?.employmentHistory && this.employmentInfo) {
            this.employmentInfo.runValidation();
            checkValids.push(this.employmentInfo.employeeForm.valid);
        }

        if (this.finalSubmitPageConfig?.residencyHistory && this.residenceInfo) {
            this.residenceInfo.runValidation();
            checkValids.push(this.residenceInfo.residenceForm.valid);
            if (this.appConfig.coBoMailingAddress && this.residenceInfo.mailingAddressForm) {
                checkValids.push(this.residenceInfo.mailingAddressForm.valid);
            }
        }
        // if anyone is invalid then return false;
        console.log('checkValids.filter(valid => !valid).length > 0)>>>>>>>>>>>>>>', checkValids.filter(valid => !valid).length > 0);
        return !(checkValids.filter(valid => !valid).length > 0);
    }

    onCitizenShipChange(event) {
        this.applicant.citizenshipStatusDesc = event.value;
    }

    getApplicantDetails() {
        if (this.finalSubmitPageConfig.employmentHistory && this.employmentInfo) {
            const employeeFormValues = this.employmentInfo.employeeForm.getRawValue();
            if (employeeFormValues && employeeFormValues.employeeAdd && employeeFormValues.employeeAdd.length > 0) {
                const employmentAdd = [];
                employeeFormValues.employeeAdd.forEach(value => {
                    employmentAdd.push({
                        ...(value.employmentId ? {employmentId: value.employmentId} : {}),
                        empType: value.employeeType,
                        length: value.employmentLength,
                        empName: this.employmentInfo.getEmployerName(value.employeeType, value.employerName),
                        salary: value.salary,
                        schedule: value.schedule || 'MONTHLY',
                        position: value.position,
                        empStatus: value.empStatus || 'P'
                    });
                });
                this.applicant.employers = employmentAdd;
            }

        }

        if (this.finalSubmitPageConfig.residencyHistory && this.residenceInfo) {
            const residenceAdd = [];
            const address = [];
            const residenceFormValues = this.residenceInfo.residenceForm.getRawValue();
            if (residenceFormValues && residenceFormValues.residenceAdd && residenceFormValues.residenceAdd.length > 0) {
                residenceFormValues.residenceAdd.forEach(value => {
                    residenceAdd.push({
                        length: +(value.residenceLength || 0),
                        status: value.residenceStatus || null,
                        residenceId: value.residenceId,
                    });

                    address.push({
                        streetAddress: value.streetAddress,
                        zipCode: value.zipCode,
                        city: value.city,
                        state: value.state,
                        county: value.county,
                        country: value.country
                    });
                });
                if (residenceAdd[0] && address[0]) {
                    this.applicant.residence = {...this.applicant.residence, ...residenceAdd[0]};
                    this.applicant.address = {...this.applicant.address, ...address[0]};
                }
                // delete first address and residence to use part residences
                residenceAdd.shift();
                address.shift();
                const pastResidences = [];
                address.forEach((ad, i) => {
                    pastResidences.push({
                        ...(residenceAdd[i].residenceId ? {residenceId: residenceAdd[i].residenceId} : {}),
                        status: residenceAdd[i].status,
                        length: residenceAdd[i].length,
                        applicationId: this.keyIdentifier.applicationId,
                        address: {
                            streetAddress: ad.streetAddress,
                            city: ad.city,
                            state: ad.state,
                            zipCode: ad.zipCode,
                            county: ad.county,
                            country: ad.country,
                            applicationId: this.keyIdentifier.applicationId
                        }
                    });
                });

                this.applicant.pastResidenceDetails = pastResidences;
            }
        }

        if (this.appConfig.coBoMailingAddress) {
            let mailingAddressValues: any;
            if (this.residenceInfo.sameMailingAsPrimary && this.residenceInfo.primaryMailingAddress) {
                mailingAddressValues = this.residenceInfo.primaryMailingAddress;
            } else if (!this.residenceInfo.sameMailAddress) {
                mailingAddressValues = this.residenceInfo.mailingAddressData;
            } else {
                mailingAddressValues = this.applicant.address;
            }
            // Key off the JO's own existing COMMUNICATION-usage addressId (not the source record's
            // addressId) so an existing saved mailing address is updated in place rather than
            // duplicated - this matters when the source is the primary applicant's address, whose
            // addressId belongs to a different applicant entirely.
            const existingMailingAddressId = this.residenceInfo.mailingAddressData?.addressId;
            const mailingAddress: AddressModel = {
                streetAddress: mailingAddressValues.streetAddress,
                city: mailingAddressValues.city,
                state: mailingAddressValues.state,
                zipCode: mailingAddressValues.zipCode,
                county: mailingAddressValues.county,
                country: mailingAddressValues.country,
                addressUsage: 'COMMUNICATION',
                ...(existingMailingAddressId && {addressId: existingMailingAddressId})
            };
            const mailingAddressIndex = this.applicant.additionalAddressDetails.findIndex(add => add.addressUsage === 'COMMUNICATION');
            if (mailingAddressIndex > -1) {
                this.applicant.additionalAddressDetails[mailingAddressIndex] = mailingAddress;
            } else {
                this.applicant.additionalAddressDetails.push(mailingAddress);
            }
        }
        this.applicant.applicantType = ApplicantType.COBORROWER;
        return this.applicant;
    }


}
